/**
 * @file AbandonEvent.jsx
 * @description Main page component for the Abandon Event insight module.
 *              Fetches text (ComplianceDate) and email (ContactDate) abandon event data on mount,
 *              applies client-side date filtering, and displays KPI cards, event distribution %
 *              horizontal bar chart, events-over-time composed chart, and a paginated detail table.
 *
 * @component AbandonEvent
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, IconButton, Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart,
  Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  fetchAbandonEventText,
  fetchAbandonEventEmail,
  fetchAbandonEventConversations,
} from "../../../services/insightModule/insightService";
import DatePickerComponent from "../../../common/components/DatePickerComponent";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import { STRINGS } from "../../../common/constants/commonConstants";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";
const GREEN  = "#1D9E75";
const PURPLE = "#7F77DD";
const TEAL   = "#85B7EB";
const GRAY   = "#888780";

const EVENT_COLORS = [
  BLUE, PINK, GREEN, AMBER, PURPLE, TEAL, GRAY,
  "#185FA5","#0F6E56","#993556","#3C3489","#633806",
];

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
  if (!val) return null;
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

function fmtDate(val) {
  const d = parseLocalDate(val);
  if (!d || isNaN(d)) return val || "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function fmt(n, dec = 2) {
  const num = Number(n);
  return isNaN(num) ? "—" : num.toFixed(dec);
}

function filterByDate(rows, dateField, startDate, endDate) {
  const start = startDate
    ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0)
    : null;
  const end = endDate
    ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999)
    : null;
  return (rows || []).filter(row => {
    const d = parseLocalDate(row[dateField]);
    if (!d || isNaN(d.getTime())) return false;
    if (start && d < start) return false;
    if (end   && d > end)   return false;
    return true;
  });
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        background: "#f7f7f7", borderRadius: 2,
        p: "10px 14px", minWidth: 0, flex: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "background .15s",
        "&:hover": onClick ? { background: "#efefef" } : {},
      }}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const AbandonEvent = () => {
  const [startDate, setStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate,   setEndDate]   = useState(() => new Date());
  const [channel,   setChannel]   = useState("Texts");
  const [allText,   setAllText]   = useState([]);
  const [allEmail,  setAllEmail]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [page,      setPage]      = useState(0);
  const [drill,     setDrill]     = useState(null);
  const rowsPerPage = 25;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, e] = await Promise.all([
        fetchAbandonEventText(),
        fetchAbandonEventEmail(),
      ]);
      setAllText(Array.isArray(t)  ? t : []);
      setAllEmail(Array.isArray(e) ? e : []);
    } catch (err) {
      console.error(err);
      setError("Failed to load abandon event data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Texts use ComplianceDate, emails use ContactDate
  const data = useMemo(() => {
    if (channel === "Emails") {
      return filterByDate(allEmail, "ContactDate", startDate, endDate);
    }
    return filterByDate(allText, "ComplianceDate", startDate, endDate);
  }, [channel, allText, allEmail, startDate, endDate]);

  const dateField = channel === "Emails" ? "ContactDate" : "ComplianceDate";

  // ── Aggregated event totals (for bar chart + KPIs) ─────────────
  const { eventTotals, grandTotal } = useMemo(() => {
    const map = {};
    let grand = 0;
    data.forEach(r => {
      const ev = (r.Event || "").trim();
      if (!ev) return;
      if (!map[ev]) map[ev] = { count: 0, total: 0 };
      map[ev].count += safeNum(r.EventCount);
      map[ev].total += safeNum(r.TotalRecords ?? r.TotalRecord);
      grand += safeNum(r.EventCount);
    });
    const totals = Object.entries(map)
      .map(([name, v]) => ({
        name,
        count: v.count,
        total: v.total,
        pct: v.total > 0 ? v.count / v.total * 100 : 0,
      }))
      .sort((a, b) => b.pct - a.pct);
    return { eventTotals: totals, grandTotal: grand };
  }, [data]);

  // ── Chart data — events over time ─────────────────────────────
  const timeChartData = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const d   = parseLocalDate(r[dateField]);
      if (!d || isNaN(d)) return;
      const key = d.getTime();
      if (!map[key]) map[key] = {
        _ts: key, _convDate: r[dateField],
        date: fmtDate(r[dateField]), total: 0,
      };
      map[key].total += safeNum(r.EventCount);
      const ev = (r.Event || "").trim();
      if (ev) map[key][ev] = (map[key][ev] || 0) + safeNum(r.EventCount);
    });
    return Object.values(map).sort((a, b) => a._ts - b._ts);
  }, [data, dateField]);

  // ── Pagination ─────────────────────────────────────────────────
  const sortedData = useMemo(() =>
    [...data].sort((a, b) => {
      const da = parseLocalDate(a[dateField]);
      const db = parseLocalDate(b[dateField]);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    })
  , [data, dateField]);

  const pagedData  = useMemo(() =>
    sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedData, page]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ── Drill-down ─────────────────────────────────────────────────
  const openDrill = useCallback(({ event = null, date = null }) => {
    const evIdx = event ? eventTotals.findIndex(e => e.name === event) : -1;
    const evColor = evIdx >= 0
      ? EVENT_COLORS[evIdx % EVENT_COLORS.length]
      : BLUE;

    const title =
      event && date ? "Conversations by event & date" :
      event         ? `Conversations: ${event}` :
                      `Conversations on ${fmtDate(date)}`;

    const dStart = date ? new Date(date) : startDate;
    const dEnd   = date ? new Date(date) : endDate;

    setDrill({
      title,
      metric: event ? { label: "Event", value: event, color: evColor } : null,
      context: [
        date && { label: "Date", value: fmtDate(date) },
        !date && startDate && { label: "From", value: fmtDate(startDate) },
        !date && endDate   && { label: "To",   value: fmtDate(endDate) },
      ].filter(Boolean),
      channel,
      // Color the event chip on each conversation card to match the bar/KPI palette
      emotionColors: Object.fromEntries(
        eventTotals.map((e, i) => [e.name, EVENT_COLORS[i % EVENT_COLORS.length]])
      ),
      fetchData: () => fetchAbandonEventConversations({
        event,
        startDate:   dStart ? dStart.toISOString() : null,
        endDate:     dEnd   ? dEnd.toISOString()   : null,
        channeltype: channel,
      }),
    });
  }, [eventTotals, startDate, endDate, channel]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // 40px drill icon column at the start
  const COLS    = "40px 100px 1fr 90px 90px 100px";
  const HEADERS = ["", "Date", "Event", "Event Count", "Event Rate", "Total Records"];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d && d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => setPage(0)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        {/* Channel toggle */}
        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => { setChannel(ch); setPage(0); }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  channel === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  channel === ch ? BLUE : "transparent",
              color:       channel === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs ──────────────────────────────────── */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <KpiCard label="Total events"   value={grandTotal.toLocaleString()} color={BLUE}   />
            <KpiCard label="Unique events"  value={eventTotals.length}          color={PURPLE} />
            {eventTotals.slice(0, 3).map((e, i) => (
              <KpiCard
                key={e.name}
                label={e.name}
                value={`${e.count.toLocaleString()} (${fmt(e.pct, 1)}%)`}
                color={EVENT_COLORS[i % EVENT_COLORS.length]}
                onClick={() => openDrill({ event: e.name })}
              />
            ))}
          </Box>

          {/* ── EVENT % HORIZONTAL BAR (click bar → drill event) ── */}
          <SectionTitle>Event distribution %</SectionTitle>

          <ChartCard height={Math.max(160, eventTotals.length * 44)}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={eventTotals}
                layout="vertical"
                margin={{ top: 4, right: 60, bottom: 4, left: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" horizontal={false} />
                <XAxis type="number" tick={axisStyle} domain={[0, 100]}
                  tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="name"
                  tick={{ fontSize: 11, fill: "#555" }} width={160} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n, p) => [
                    `${fmt(p.payload.pct)}% (${p.payload.count.toLocaleString()} / ${p.payload.total.toLocaleString()})`,
                    "Event"
                  ]}
                />
                <Bar dataKey="pct" name="%" radius={[0,4,4,0]} cursor="pointer">
                  {eventTotals.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={EVENT_COLORS[i % EVENT_COLORS.length]}
                      onClick={() => openDrill({ event: entry.name })}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── EVENTS OVER TIME (click point → drill date) ──── */}
          <SectionTitle>Event count over time</SectionTitle>

          <ChartCard height={220}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={timeChartData}
                style={{ cursor: "pointer" }}
                onClick={(state) => {
                  const dt = state?.activePayload?.[0]?.payload?._convDate;
                  if (dt) openDrill({ date: dt });
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                {eventTotals.slice(0, 6).map((ev, i) => (
                  <Bar key={ev.name} dataKey={ev.name} stackId="a"
                    fill={EVENT_COLORS[i % EVENT_COLORS.length]}
                    radius={i === Math.min(5, eventTotals.length-1) ? [3,3,0,0] : [0,0,0,0]}
                  />
                ))}
                <Line type="monotone" dataKey="total" name="Total"
                  stroke={AMBER} dot={{ r: 3 }} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE ────────────────────────────────────── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
              alignItems: "center",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={i} sx={{ textAlign: i <= 2 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {/* Rows */}
            {pagedData.map((row, i) => {
              const eventName = (row.Event || "").trim();
              const evIdx = eventTotals.findIndex(e => e.name === eventName);
              const evColor = EVENT_COLORS[evIdx >= 0 ? evIdx % EVENT_COLORS.length : 0];
              const drillable = !!eventName;
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  px: 2, py: "6px",
                  fontSize: 12,
                  borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                  alignItems: "center",
                }}>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => drillable
                        ? openDrill({ event: eventName, date: row[dateField] })
                        : openDrill({ date: row[dateField] })}
                      sx={{ width: 24, height: 24 }}
                      title={drillable
                        ? "View conversations for this event on this date"
                        : "View conversations on this date"}
                    >
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Box>{fmtDate(row[dateField])}</Box>
                  <Box
                    onClick={drillable
                      ? () => openDrill({ event: eventName, date: row[dateField] })
                      : undefined}
                    sx={{
                      color: evColor, fontWeight: 500,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      cursor: drillable ? "pointer" : "default",
                      "&:hover": drillable ? { textDecoration: "underline" } : {},
                    }}
                  >
                    {row.Event || "—"}
                  </Box>
                  <Box sx={{ textAlign: "right", color: BLUE, fontWeight: 500 }}>
                    {safeNum(row.EventCount).toLocaleString()}
                  </Box>
                  <Box sx={{ textAlign: "right", color: AMBER }}>
                    {row.EventRate != null ? `${fmt(row.EventRate, 1)}%` : "—"}
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    {safeNum(row.TotalRecords ?? row.TotalRecord).toLocaleString()}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: "flex", justifyContent: "flex-end",
              alignItems: "center", gap: 1, mt: 1, fontSize: 12,
              color: "text.secondary",
            }}>
              <Box
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page === 0 ? "default" : "pointer",
                  opacity: page === 0 ? 0.4 : 1,
                  "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                }}
              >
                ‹
              </Box>
              <span>Page {page + 1} of {totalPages}</span>
              <Box
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page >= totalPages - 1 ? "default" : "pointer",
                  opacity: page >= totalPages - 1 ? 0.4 : 1,
                  "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                }}
              >
                ›
              </Box>
            </Box>
          )}
        </>
      )}

      {/* ── Drill-down popup ───────────────────────────────────── */}
      {drill && (
        <ConversationDrillDownDialog
          open={!!drill}
          onClose={() => setDrill(null)}
          {...drill}
        />
      )}
    </Box>
  );
};

export default AbandonEvent;